import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useCountries } from '@/application/queries'
import { useFilter } from '@/application/use-filter'
import { toCountryCodes } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[]
) {
  return [
    'productMetrics',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections
  ]
}

export function useProductMetrics(
  startDate: string | undefined,
  endDate: string | undefined
) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  function callProductMetrics() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getProductsMetrics({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const productMetricsQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callProductMetrics,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const totalItemCount = productMetricsQuery.data?.reasons.reduce(
    (acc, reason) => acc + reason.totalReturnedItems,
    0
  )

  return {
    totalItemCount,
    products: productMetricsQuery.data?.products || [],
    reasons: productMetricsQuery.data?.reasons || [],
    productMetrics: productMetricsQuery.data,
    loadingProductMetrics: productMetricsQuery.isLoading || loadingCountries
  }
}
